import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Users, CreditCard, User, Globe, BookOpen, Clock, LogOut, X, Menu, MapPin, Eye, Activity, Monitor, Smartphone, LayoutGrid, Search, Sun, MessageSquareText } from 'lucide-react';
import { useLang } from '../i18n';
import { useAuth } from '../AuthContext';
import { api, prefetchShared, setAfterWrite } from '../api';
import { prefetchPaths } from '../lib/prefetchPaths';
import { cn } from '../lib/utils';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { MobileHeaderContext, LeadingButtons, BanknotePlus, parseAmount, fmtAmount } from './MobileHeader';
import GlobalSearch from './GlobalSearch';
import Cashbox from './Cashbox';

const ROLE_LABELS = { admin: 'Admin', assistant: 'Assistant', viewer: 'Observateur' };


const ALL_NAV = [
  { path: '/aujourdhui',       icon: Sun,        key: 'nav.today',           roles: ['admin'] },
  { path: '/messages-types',   icon: MessageSquareText, key: 'nav.messagesTypes', roles: ['admin'] },
  { path: '/planning',         icon: Calendar,   key: 'nav.planning',        roles: ['admin'] },
  { path: '/group-classes',    icon: BookOpen,   key: 'nav.groupClasses',    roles: ['admin','assistant','viewer'] },
  { path: '/private-sessions', icon: Clock,      key: 'nav.privateSessions', roles: ['admin'] },
  { path: '/students',         icon: Users,      key: 'nav.students',        roles: ['admin'] },
  { path: '/finance',          icon: CreditCard, key: 'nav.finance',         roles: ['admin'] },
  { path: '/habits',           icon: Activity,     key: 'nav.habits',          roles: ['admin'] },
  { path: '/planner',          icon: LayoutGrid,   key: 'nav.planner',         roles: ['admin'] },
  { path: '/profile',          icon: User,         key: 'nav.profile',         roles: ['admin'] },
];

export default function AppShell({ children }) {
  const { t, lang, setLang } = useLang();
  const { user, logout, previewRole, setPreviewRole, effectiveRole, settings, updateSettings } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [simulateMobile, setSimulateMobile] = useState(() => localStorage.getItem('ds_layout') === 'mobile');

  // En-tête mobile : les pages y placent titre et actions via <MobileHeader> (portail)
  const [headerSlot, setHeaderSlot] = useState(null);
  const [headerCount, setHeaderCount] = useState(0);
  const registerHeader = useCallback(() => {
    setHeaderCount(c => c + 1);
    return () => setHeaderCount(c => c - 1);
  }, []);
  // Recherche globale (🔍), caisse (💵+) et recherche propre à la page (ligne « Aussi : … »)
  const [search, setSearch] = useState(null);           // null = fermée, sinon { q, scope }
  const [cashboxOpen, setCashboxOpen] = useState(false);
  const [cashboxAmount, setCashboxAmount] = useState(null);  // montant tapé dans la recherche
  const [cashboxPendingOpen, setCashboxPendingOpen] = useState(false);  // v2.25 : « À attribuer » déplié quand on vient pour attribuer
  const [cashboxCourse, setCashboxCourse] = useState(null);
  const [cashPending, setCashPending] = useState(0);     // encaissements « à attribuer »
  const [pageSearch, setPageSearch] = useState(null);    // { q, scope } publié par la page
  const isAdmin = (effectiveRole || user?.role) === 'admin';
  const refreshCashPending = useCallback(() => {
    if (!isAdmin) return;
    api.get('/cashbox/pending').then(l => setCashPending((l || []).length)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { refreshCashPending(); }, [refreshCashPending]);

  // v2.14 : Google Agenda + Tasks reliés automatiquement — seulement pour le compte principal (le serveur décide),
  // une seule fois par visite (si on refuse chez Google, on ne reboucle pas), et jamais sur la copie locale de test.
  useEffect(() => {
    if (user?.role !== 'admin' || ['localhost', '127.0.0.1'].includes(window.location.hostname)) return;
    let asked = false;
    try { asked = !!sessionStorage.getItem('google-auto'); } catch { /* navigation privée */ }
    if (asked) return;
    api.get('/auth/google/status').then(s => {
      if (!s?.should_connect) return;
      try { sessionStorage.setItem('google-auto', '1'); } catch { /* navigation privée */ }
      return api.get('/auth/google/login-tasks').then(r => { if (r?.authorization_url) window.location.href = r.authorization_url; });
    }).catch(() => {});
  }, [user?.role]);

  // v2.13 : précharge les grandes listes (élèves, cours, finance…) et les garde fraîches tant que l'onglet est visible.
  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    const warm = () => {
      if (document.visibilityState !== 'visible') return;
      // v2.18 : + le Planning du mois, Google et le tableau des revenus des Collectifs
      prefetchShared().then(() => api.get('/group-courses')).then(c => prefetchShared(prefetchPaths(c))).catch(() => {});
    };
    const first = setTimeout(warm, 300);  // v2.22 : tout de suite à l'ouverture
    setAfterWrite(warm);                   // v2.22 : et après chaque modification
    const every = setInterval(warm, 50_000);
    document.addEventListener('visibilitychange', warm);  // v2.17 : retour sur l'onglet → listes fraîches tout de suite
    return () => { clearTimeout(first); clearInterval(every); setAfterWrite(null); document.removeEventListener('visibilitychange', warm); };
  }, [user?.role]);
  const openSearch = useCallback((q = '', scope = 'all') => { setMenuOpen(false); setSearch({ q, scope }); }, []);
  const openCashbox = useCallback((amount = null, pendingOpen = false) => { setMenuOpen(false); setSearch(null); setCashboxAmount(amount); setCashboxPendingOpen(pendingOpen); setCashboxOpen(true); }, []);
  useEffect(() => {  // PC : ⌘K / Ctrl+K ouvre la recherche
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); openSearch(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSearch]);
  const headerCtx = useMemo(() => ({
    slot: headerSlot, register: registerHeader, openMenu: () => setMenuOpen(o => !o),
    openSearch, openCashbox, isAdmin, pendingCount: cashPending, setPageSearch, setCashboxCourse,
  }), [headerSlot, registerHeader, openSearch, openCashbox, isAdmin, cashPending]);

  useEffect(() => {
    document.documentElement.classList.toggle('simulate-mobile', simulateMobile);
  }, [simulateMobile]);
  // v2.16 — le PC reprend l'affichage du téléphone sur toutes les pages (le téléphone est la référence) ;
  // seul le menu de gauche reste propre au PC.
  const alignMobile = !simulateMobile;
  useEffect(() => {
    document.documentElement.classList.toggle('align-mobile', alignMobile);
  }, [alignMobile]);

  const toggleLayout = () => {
    const next = !simulateMobile;
    setSimulateMobile(next);
    localStorage.setItem('ds_layout', next ? 'mobile' : 'desktop');
  };

  const role = effectiveRole || 'viewer';
  const NAV = ALL_NAV.filter(n => n.roles.includes(role));

  // Settings depuis le contexte (DB-synced, fallback localStorage immédiat)
  const navConfig     = settings.nav_config || null;
  const mobileNavCount = settings.nav_mobile_count || 5;

  // Sync cross-tabs : quand un AUTRE onglet modifie localStorage, resynchroniser le contexte
  useEffect(() => {
    const handler = (e) => {
      if (e?.key && !['ds_nav_config', 'ds_nav_mobile_count'].includes(e.key)) return;
      const nc  = (() => { try { return JSON.parse(localStorage.getItem('ds_nav_config') || 'null'); } catch { return null; } })();
      const nmc = parseInt(localStorage.getItem('ds_nav_mobile_count') || '5', 10);
      updateSettings({ nav_config: nc, nav_mobile_count: nmc });
    };
    // 'storage' fire uniquement dans les AUTRES onglets → pas de double-write
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [updateSettings]);

  // Un menu réordonné avant la 3.0 ne connaît pas « Aujourd'hui » : il passe en tête.
  const unknownToday = navConfig && !navConfig.includes('/aujourdhui') ? NAV.filter(n => n.path === '/aujourdhui') : [];
  const orderedNav = navConfig
    ? unknownToday.concat(navConfig
        .map(path => NAV.find(n => n.path === path))
        .filter(Boolean)
        .concat(NAV.filter(n => !navConfig.includes(n.path) && !unknownToday.includes(n))))
    : NAV;

  const MOBILE_NAV = orderedNav.slice(0, mobileNavCount);
  // v2.25 : l'onglet de la page affichée reste visible dans la barre qui glisse
  useEffect(() => {
    const el = document.querySelector('[data-testid=bottom-nav-scroll] a.active');
    if (el) el.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [location.pathname]);
  const isPreviewMode = user?.role === 'admin' && previewRole;

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/admin/registrations')
      .then(reqs => setPendingCount((reqs || []).filter(r => r.status === 'pending').length))
      .catch(() => {});
  }, [user]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {isPreviewMode && (
        <div className="bg-amber-500 text-white text-center text-xs py-1.5 font-semibold flex items-center justify-center gap-3 shrink-0">
          <Eye size={13} />
          Prévisualisation — vue en tant que : <strong>{ROLE_LABELS[previewRole]}</strong>
          <button onClick={() => { setPreviewRole(null); navigate('/'); }}
            className="px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs ml-2 transition-colors">
            Quitter
          </button>
        </div>
      )}
      {user?.is_demo && (
        <div className="bg-slate-700 text-white text-center text-xs py-1.5 font-semibold flex items-center justify-center gap-2 shrink-0">
          <Eye size={13} />
          Mode démo · Données anonymisées
        </div>
      )}
      <div className="flex flex-1">
      {/* Desktop sidebar */}
      <aside className={cn(simulateMobile ? 'hidden' : 'hidden lg:flex', 'flex-col w-60 bg-white border-r border-slate-100 fixed h-full z-20')}>
        <div className="h-16 flex items-center px-6 border-b border-slate-100 gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0288D1] flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm font-manrope">DS</span>
          </div>
          <div>
            <div className="font-bold text-slate-900 text-sm font-manrope leading-tight">Dance School</div>
            <div className="text-xs text-slate-400">Cockpit</div>
          </div>
        </div>
        {/* Recherche (⌘K) et caisse, accessibles depuis toutes les pages */}
        <div className="px-3 pt-3 space-y-2">
          <button onClick={() => openSearch()} data-testid="sidebar-search-btn"
            className="w-full flex items-center gap-2 h-10 px-3 rounded-xl border border-slate-200 text-sm text-slate-400 hover:border-slate-300">
            <Search size={15} /> <span className="flex-1 text-left">Rechercher…</span>
            <kbd className="text-[10px] font-semibold text-slate-400 border border-slate-200 rounded px-1">⌘K</kbd>
          </button>
          {isAdmin && (
            <button onClick={() => openCashbox()} data-testid="sidebar-cashbox-btn"
              className="w-full flex items-center gap-2 h-10 px-3 rounded-xl bg-[#26A69A] text-white text-sm font-semibold hover:bg-[#219e92]">
              <BanknotePlus size={16} /> <span className="flex-1 text-left ml-1">Encaisser</span>
              {cashPending > 0 && <span className="text-[10px] font-bold bg-white text-[#26A69A] px-1.5 py-0.5 rounded-full">{cashPending} à attribuer</span>}
            </button>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {orderedNav.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-colors duration-150',
                isActive
                  ? 'bg-[#0288D1] text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
            >
              <item.icon size={18} />
              <span>{t(item.key)}</span>
              {item.path === '/profile' && pendingCount > 0 && (
                <span className="ml-auto text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 space-y-1">
          {user?.role === 'admin' && !isPreviewMode && (
            <div className="px-3 py-2 rounded-xl bg-slate-50 mb-1">
              <p className="text-[10px] text-slate-400 mb-1.5 font-semibold uppercase tracking-wide">Prévisualiser en tant que</p>
              <div className="flex gap-1">
                {['assistant', 'viewer'].map(r => (
                  <button key={r} onClick={() => { setPreviewRole(r); navigate('/group-classes'); }}
                    data-testid={`preview-as-${r}`}
                    className="flex-1 text-[10px] font-semibold py-1 rounded-lg border border-slate-200 hover:border-[#0288D1] hover:text-[#0288D1] text-slate-500 transition-colors bg-white">
                    {ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {user && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 mb-1">
              {user.picture
                ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                : <div className="w-7 h-7 rounded-full bg-[#0288D1] flex items-center justify-center shrink-0">
                    <span className="text-white text-xs font-bold">{(user.name||user.email)[0].toUpperCase()}</span>
                  </div>
              }
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-700 truncate">{user.name || user.email}</div>
                <div className="text-[10px] text-slate-400 capitalize">
                  {isPreviewMode ? <span className="text-amber-500">{ROLE_LABELS[previewRole]} (aperçu)</span> : user.role}
                </div>
              </div>
              <button onClick={logout} title="Déconnexion"
                className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                <LogOut size={13} />
              </button>
            </div>
          )}
          <button
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-800 w-full px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
            data-testid="lang-toggle-desktop"
          >
            <Globe size={15} />
            <span>{lang === 'fr' ? 'Switch to English' : 'Passer en français'}</span>
          </button>
          <button
            onClick={toggleLayout}
            className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-800 w-full px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
            data-testid="layout-toggle-desktop"
            title={simulateMobile ? 'Revenir en vue bureau' : 'Simuler vue mobile'}
          >
            {simulateMobile ? <Monitor size={15} /> : <Smartphone size={15} />}
            <span>{simulateMobile ? 'Vue bureau' : 'Vue mobile'}</span>
          </button>
        </div>
      </aside>

      <MobileHeaderContext.Provider value={headerCtx}>
      {/* Main content */}
      <main className={cn('flex-1 min-w-0 min-h-screen',!simulateMobile && 'lg:ml-60 lg:pb-0')}
        style={{ paddingBottom: !simulateMobile ? undefined : 'calc(3.5rem + max(4px, calc(env(safe-area-inset-bottom, 0px) - 16px)))', overflowX: 'clip' }}>
        <style>{`@media (max-width: 1023px) { main { padding-bottom: calc(3.5rem + max(4px, calc(env(safe-area-inset-bottom, 0px) - 16px))) !important; } }`}</style>
        {/* Mobile header — une seule ligne : ☰/← · titre de la page · actions (voir MobileHeader.jsx) */}
        {/* v2.16 : la même barre du haut sur PC que sur téléphone, sur toutes les pages */}
        <header className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-100 z-10"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="h-12 flex items-center gap-1 px-2">  {/* v2.25 : barre plus fine (56 → 48 px) */}
            <div ref={setHeaderSlot} className="relative flex-1 min-w-0 flex items-center gap-1" />
            {headerCount === 0 && (
              <div className="relative flex-1 flex items-center gap-1">
                <LeadingButtons />
                <span className="absolute left-1/2 -translate-x-1/2 font-extrabold text-slate-900 text-lg font-manrope pointer-events-none">Dance School</span>
              </div>
            )}
          </div>
        </header>
        {pageSearch && (
          <SearchActions q={pageSearch.q} scope={pageSearch.scope} isAdmin={isAdmin} pendingCount={cashPending}
            onOpenSearch={() => openSearch(pageSearch.q)} onCashbox={openCashbox} />
        )}

        {/* Mobile drawer menu — only shows items NOT in the bottom tab bar */}
        {menuOpen && (
          <>
            <div className="fixed inset-0 bg-black/30 z-30" onClick={() => setMenuOpen(false)} />
            <div className={cn(!simulateMobile ? 'lg:hidden' : '', 'fixed top-12 left-0 w-72 bg-white shadow-2xl z-40 rounded-br-2xl overflow-hidden')}
              style={{ top: 'calc(3rem + env(safe-area-inset-top, 0px))' }}
              data-testid="mobile-drawer">
              {/* User info */}
              {user && (
                <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-100">
                  <div className="w-8 h-8 rounded-full bg-[#0288D1]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#0288D1]">{(user.name || user.email || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{user.name || user.email}</div>
                    <div className="text-[10px] text-slate-400 capitalize">{ROLE_LABELS[role] || role}</div>
                  </div>
                </div>
              )}
              {/* Only nav items NOT already in bottom tab bar */}
              <nav className="p-3 space-y-0.5">
                {orderedNav.slice(MOBILE_NAV.length).map(item => {
                  const isActive = location.pathname.startsWith(item.path);
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/'}
                      onClick={() => setMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-colors',
                        isActive
                          ? 'bg-[#0288D1] text-white'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      )}
                    >
                      <item.icon size={17} />
                      <span>{t(item.key)}</span>
                    </NavLink>
                  );
                })}
              </nav>
              <div className="border-t border-slate-100 p-3 space-y-1">
                <button
                  onClick={() => { toggleLayout(); setMenuOpen(false); }}
                  className="flex items-center gap-2 text-xs text-slate-500 w-full px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                  data-testid="layout-toggle-mobile"
                >
                  {simulateMobile ? <Monitor size={15} /> : <Smartphone size={15} />}
                  <span>{simulateMobile ? 'Vue bureau' : 'Vue mobile'}</span>
                </button>
                <button
                  onClick={() => { setLang(lang === 'fr' ? 'en' : 'fr'); setMenuOpen(false); }}
                  className="flex items-center gap-2 text-xs text-slate-500 w-full px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <Globe size={15} />
                  <span>{lang === 'fr' ? 'Switch to English' : 'Passer en français'}</span>
                </button>
                {user && (
                  <button onClick={() => { logout(); setMenuOpen(false); }}
                    className="flex items-center gap-2 text-xs text-red-500 w-full px-4 py-2.5 rounded-xl hover:bg-red-50 transition-colors">
                    <LogOut size={15} />
                    <span>Déconnexion</span>
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
      <GlobalSearch open={!!search} initialQuery={search?.q || ''} initialScope={search?.scope || 'all'}
        isAdmin={isAdmin} pendingCount={cashPending} onCashbox={openCashbox} onClose={() => setSearch(null)} />
      {isAdmin && (
        <Cashbox open={cashboxOpen} initialAmount={cashboxAmount} pendingOpen={cashboxPendingOpen} courseId={cashboxCourse}
          onClose={() => setCashboxOpen(false)} onChanged={refreshCashPending} />
      )}
      </MobileHeaderContext.Provider>

      {/* Mobile bottom nav */}
      <nav
        className={cn(!simulateMobile ? 'lg:hidden' : '', 'fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 z-20 safe-area-bottom')}
        style={{ paddingBottom: 'max(4px, calc(env(safe-area-inset-bottom, 0px) - 16px))' }}
        data-testid="bottom-nav"
      >
        {/* v2.25 : barre plus fine ; tous les onglets y sont, on glisse de gauche à droite pour voir les suivants */}
        <div className="h-[50px] flex overflow-x-auto no-scrollbar snap-x snap-mandatory overscroll-x-contain" data-testid="bottom-nav-scroll">
        {orderedNav.map(item => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className="shrink-0 snap-start flex flex-col items-center justify-center gap-0.5 relative"
              style={{ width: `${100 / Math.max(1, MOBILE_NAV.length)}%` }}
              data-testid={`nav-${item.path.replace(/\//g, '') || 'home'}`}
            >
              <item.icon size={19} className={isActive ? 'text-[#0288D1]' : 'text-slate-400'} />
              <span className={cn('text-[9px] font-semibold', isActive ? 'text-[#0288D1]' : 'text-slate-400')}>
                {t(item.key)}
              </span>
              {isActive && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#0288D1] rounded-full" />}
            </NavLink>
          );
        })}
        </div>
      </nav>
      </div>
    </div>
  );
}

// Ligne d'actions sous la recherche d'une page qui a sa propre liste :
// « Encaisser X CHF » si un montant est tapé, « À attribuer (n) », et « Aussi : … » (résultats des autres types)
const ALSO_LABELS = { students: ['élève', 'élèves'], courses: ['cours', 'cours'], private_classes: ['privé', 'privés'],
  festivals: ['festival', 'festivals'], events: ['événement', 'événements'], tasks: ['tâche', 'tâches'] };

function SearchActions({ q, scope, isAdmin, pendingCount, onOpenSearch, onCashbox }) {
  const [counts, setCounts] = useState(null);
  const amount = isAdmin ? parseAmount(q) : null;
  useEffect(() => {
    if (q.trim().length < 2) { setCounts(null); return; }
    let alive = true;
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then(r => { if (alive) setCounts(Object.fromEntries(Object.entries(r).filter(([k, v]) => k !== scope && v.length).map(([k, v]) => [k, v.length]))); })
        .catch(() => alive && setCounts(null));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, scope]);
  const also = counts && Object.keys(counts).length
    ? Object.entries(counts).map(([k, n]) => `${n} ${ALSO_LABELS[k][n > 1 ? 1 : 0]}`).join(' · ') : null;
  if (!amount && !(isAdmin && pendingCount > 0 && !q) && !also) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-slate-100 overflow-x-auto no-scrollbar" data-testid="search-actions">
      {amount && (
        <button onClick={() => onCashbox(amount)} data-testid="search-cashbox-btn"
          className="shrink-0 h-9 px-3 rounded-full bg-[#26A69A] text-white text-sm font-bold flex items-center gap-1.5">
          <BanknotePlus size={16} /> <span className="ml-1">Encaisser {fmtAmount(amount)} CHF</span>
        </button>
      )}
      {isAdmin && pendingCount > 0 && !q && (
        <button onClick={() => onCashbox(null, true)} data-testid="search-pending-btn"
          className="shrink-0 h-9 px-3 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm font-semibold">
          À attribuer ({pendingCount})
        </button>
      )}
      {also && (
        <button onClick={onOpenSearch} data-testid="also-results"
          className="shrink-0 h-9 px-3 rounded-full bg-[#0288D1]/5 text-[#0288D1] text-sm font-semibold flex items-center gap-1.5">
          <Search size={14} /> Aussi : {also} →
        </button>
      )}
    </div>
  );
}
